import axios from "axios";
import React, { useEffect, useState } from "react";
import { API_URL } from "../../url/api";

interface Payment {
  id: number;
  doctor: string;
  session: {
    id: number;
    title: string | null;
    booking_time: string;
    status: false;
  };
  user: {
    id: number;
    name: string;
  };
  amount: number;
  created_at: string;
  status: boolean;
}

const PaymentList: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [withdrawing, setWithdrawing] = useState<boolean>(false);

  const [paymentList, setPaymentList] = useState<Payment[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "unpaid" | "paid"
  >("all");

  useEffect(() => {
    const fetchPayments = async () => {
      const doctorID = window.localStorage.getItem("doctorId");

      if (!doctorID) {
        setLoading(false);
        // Redirect to login page
        return;
      }

      const { data } = await axios.get(
        API_URL + "payout-list/" + doctorID + "/"
      );

      if (data) if (data.success) setPaymentList(data.response);
      setLoading(false);
    };

    fetchPayments();

    return () => {};
  }, []);

  const handleWithdraw = async () => {
    const doctorID = window.localStorage.getItem("doctorId");

    if (!doctorID) {
      setWithdrawing(false);
      // Redirect to login page
      return;
    }

    setWithdrawing(true);

    const { data } = await axios.post(API_URL + "withdraw/", {
      therapist: parseInt(doctorID),
      amount: pendingAmount,
    });

    setWithdrawing(false);

    if (data) if (data.id) window.alert("Withdrawal request sent successfully");
  };

  const filteredPayments = paymentList.filter((payment) => {
    if (selectedFilter === "all") return true;
    if (selectedFilter === "unpaid") return !payment.status;
    if (selectedFilter === "paid") return payment.status;
    return true;
  });

  const totalEarnings =
    paymentList.reduce((total, payment) => total + payment.amount, 0) || 0;
  const paidAmount =
    paymentList
      .filter((payment) => payment.status)
      .reduce((total, payment) => total + payment.amount, 0) || 0;
  const pendingAmount =
    paymentList
      .filter((payment) => !payment.status)
      .reduce((total, payment) => total + payment.amount, 0) || 0;

  return (
    <div className="container mx-auto py-2">
      <h2 className="text-xl font-bold mb-10">Earnings Summary</h2>
      <div className="grid grid-cols-3 gap-4 mb-8 space-x-0">
        <div className="bg-slate-50 shadow-md p-4 rounded-lg border-b-2 border-gray-300">
          <p className="text-sm mb-2 font-medium">Total</p>
          <p className="text-xl font-semibold">$ {totalEarnings}</p>
        </div>
        <div className="bg-slate-50 shadow-md border-b-2 border-green-300 p-4 rounded-lg">
          <p className="text-sm mb-2 font-medium">Paid</p>
          <p className="text-xl font-semibold">$ {paidAmount}</p>
        </div>
        <div className="bg-slate-50 shadow-md  border-b-2 border-red-300 p-4 rounded-lg">
          <p className="text-sm mb-2 font-medium">Pending</p>
          <p className="text-xl font-semibold">$ {pendingAmount}</p>
        </div>
      </div>

      <div className="mb-6">
        <button
          className={`mr-4 py-2 px-4 rounded ${
            selectedFilter === "all"
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setSelectedFilter("all")}
        >
          All
        </button>
        <button
          className={`mr-4 py-2 px-4 rounded ${
            selectedFilter === "unpaid"
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setSelectedFilter("unpaid")}
        >
          Unpaid
        </button>
        <button
          className={`py-2 px-4 rounded ${
            selectedFilter === "paid"
              ? "bg-blue-500 text-white"
              : "bg-gray-200 text-gray-700"
          }`}
          onClick={() => setSelectedFilter("paid")}
        >
          Paid
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full bg-white shadow-md rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
              <th className="py-3 px-6 text-left text-nowrap">User</th>
              <th className="py-3 px-6 text-left text-nowrap">Session</th>
              <th className="py-3 px-6 text-left text-nowrap">Amount</th>
              <th className="py-3 px-6 text-left text-nowrap">Date</th>
              <th className="py-3 px-6 text-left text-nowrap">Status</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 text-sm">
            {filteredPayments &&
              filteredPayments.length > 0 &&
              filteredPayments.map((payment) => (
                <tr
                  key={payment.id}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-4 px-6">{payment.user.name}</td>
                  <td className="py-4 px-6">{payment.session.booking_time}</td>
                  <td className="py-4 px-6">${payment.amount}</td>
                  <td className="py-4 px-6">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-6">
                    {payment.status ? (
                      <span className="bg-green-500 text-white py-1 px-2 rounded">
                        Paid
                      </span>
                    ) : (
                      <span className="bg-gray-500 text-white py-1 px-2 rounded">
                        Unpaid
                      </span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {!filteredPayments ||
          (filteredPayments.length === 0 && (
            <p className="text-center mt-8 text-gray-500">{`No ${
              selectedFilter === "all" ? "" : selectedFilter
            } payments found`}</p>
          ))}
      </div>

      {pendingAmount > 0 && (
        <button
          className="mt-8 bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded relative disabled:opacity-50"
          onClick={handleWithdraw}
          disabled={withdrawing}
        >
          {withdrawing ? (
            <svg
              aria-hidden="true"
              className="inline w-6 h-6 text-white animate-spin dark:text-gray-100 fill-primary"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          ) : (
            "Withdraw"
          )}
        </button>
      )}
    </div>
  );
};

export default PaymentList;
